import {ComboboxRoot} from "@base-ui/react";
import {Loader2, SearchIcon} from "lucide-react";
import {ReactNode, useMemo, useRef} from "react";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxValue,
  GroupedItem,
  blurActiveTextInput,
  useComboboxAnchor,
} from "@/components/ui/combobox";

import {InputGroupAddon} from "./input-group";

type ComboboxGenericGroupedProps<T> = {
  grouped: true;
  makeOptions: (items: T[]) => GroupedItem<T>[];
};

type ComboboxGenericSimpleProps<T> = {
  grouped?: false;
  makeOptions?: undefined;
};

type ComboboxGenericSingleProps<T> = {
  value: T | null;
  onChange: (value: T | null, eventDetails: ComboboxRoot.ChangeEventDetails) => void;
  multiple?: false;
};

type ComboboxGenericMultipleProps<T> = {
  value: T[] | null;
  /** Empty selection is coerced to `[]` before calling onChange. */
  onChange: (value: T[], eventDetails: ComboboxRoot.ChangeEventDetails) => void;
  multiple: true;
};

type ComboboxGenericProps<T> = (ComboboxGenericSingleProps<T> | ComboboxGenericMultipleProps<T>) &
  (ComboboxGenericGroupedProps<T> | ComboboxGenericSimpleProps<T>) & {
    items: T[];
    // Loading states
    isLoading?: boolean;

    // Item accessors (enforced by generic T)
    getItemKey: (item: T) => string | number;
    getItemLabel: (item: T) => string;
    isItemEqual: (item: T, selected: T | null) => boolean;

    // Customization
    renderItem?: (item: T) => ReactNode;
    placeholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    className?: string;
    listClassName?: string;
    showClear?: boolean;
    preserveCase?: boolean;
    shouldRenderSelection?: boolean;
    /** Single-select only: keep the leading search icon even when a value is selected (aligns with multi + `shouldRenderSelection={false}`). */
    leadWithSearchIcon?: boolean;
  } & Omit<
    React.ComponentProps<typeof Combobox>,
    | "items"
    | "value"
    | "itemToStringLabel"
    | "isItemEqualToValue"
    | "onValueChange"
    | "multiple"
  >;

export function ComboboxGeneric<T>({
  items,
  value,
  onChange,
  isLoading = false,
  getItemKey,
  getItemLabel,
  isItemEqual,
  renderItem,
  placeholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  listClassName,
  multiple = false,
  grouped = false,
  makeOptions,
  showClear = true,
  preserveCase = false,
  shouldRenderSelection = true,
  leadWithSearchIcon = false,
  onOpenChange: onOpenChangeProp,
  ...comboboxProps
}: ComboboxGenericProps<T>) {
  const anchor = useComboboxAnchor();
  const pendingKeyboardDismissRef = useRef(false);
  const controlledValue = (multiple ? (value ?? []) : (value ?? null)) as typeof value;

  // Base UI drops controlled values missing from `items` in a layout effect and calls
  // onValueChange(reason: "none"); if the parent keeps passing the same value, that loops.
  const comboboxItems = useMemo((): T[] | GroupedItem<T>[] => {
    if (grouped && makeOptions) {
      return makeOptions(items);
    }

    const list = items;

    if (multiple) {
      const selected = (value ?? []) as T[];
      if (!selected.length) return list;
      const missing = selected.filter(
        (selectedItem) => !list.some((item) => isItemEqual(item, selectedItem)),
      );
      return missing.length > 0 ? [...missing, ...list] : list;
    }

    const selected = value as T | null;
    if (selected == null) return list;
    if (list.some((item) => isItemEqual(item, selected))) return list;
    return [selected, ...list];
  }, [grouped, makeOptions, items, multiple, value, isItemEqual]);

  if (isLoading) {
    return (
      <div className="flex h-9 items-center justify-between rounded-md border border-solid px-3 py-1">
        <p className="text-muted-foreground text-sm">{placeholder}</p>
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
      </div>
    );
  }

  return (
    <Combobox
      items={comboboxItems}
      multiple={multiple}
      itemToStringLabel={getItemLabel as (itemValue: unknown) => string}
      isItemEqualToValue={isItemEqual as (itemValue: unknown, selectedValue: unknown) => boolean}
      value={controlledValue}
      onValueChange={(value: unknown, eventDetails: ComboboxRoot.ChangeEventDetails) => {
        // Base UI emits reason "none" when syncing controlled value — not a user action.
        if (eventDetails.reason === "none") return;

        if (multiple) {
          (onChange as ComboboxGenericMultipleProps<T>["onChange"])(
            (value as T[] | null) ?? [],
            eventDetails,
          );
        } else {
          pendingKeyboardDismissRef.current = true;
          (onChange as ComboboxGenericSingleProps<T>["onChange"])(value as T | null, eventDetails);
        }
      }}
      onOpenChange={(open, eventDetails) => {
        onOpenChangeProp?.(open, eventDetails);
        if (open || multiple) return;
        if (!pendingKeyboardDismissRef.current) return;
        pendingKeyboardDismissRef.current = false;
        blurActiveTextInput();
      }}
      {...comboboxProps}
      disabled={disabled || isLoading}
    >
      {multiple && !shouldRenderSelection ? (
        <ComboboxInput
          showClear={showClear && !disabled}
          showTrigger={!disabled}
          groupRef={anchor}
          isLoading={isLoading}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          preserveCase={preserveCase}
        >
          <InputGroupAddon align="inline-start">
            <SearchIcon className="size-4" />
          </InputGroupAddon>
        </ComboboxInput>
      ) : multiple ? (
        <ComboboxChips ref={anchor} className="w-full">
          <ComboboxValue>
            {(values: T[]) => (
              <>
                {values.map((value: T) => (
                  <ComboboxChip key={getItemKey(value)}>{getItemLabel(value)}</ComboboxChip>
                ))}
                <ComboboxChipsInput
                  placeholder={values.length ? "" : placeholder}
                  disabled={disabled}
                />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>
      ) : (
        <ComboboxInput
          showClear={showClear && !disabled}
          showTrigger={!disabled}
          groupRef={anchor}
          isLoading={isLoading}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          preserveCase={preserveCase}
        >
          {leadWithSearchIcon || !controlledValue ? (
            <InputGroupAddon align="inline-start">
              <SearchIcon className="size-4" />
            </InputGroupAddon>
          ) : null}
        </ComboboxInput>
      )}
      <ComboboxContent anchor={anchor}>
        {!isLoading ? <ComboboxEmpty>{emptyMessage}</ComboboxEmpty> : null}
        <ComboboxList className={listClassName}>
          {(item: T | GroupedItem<T>, index: number) =>
            grouped
              ? getItems({
                  grouped,
                  group: item as GroupedItem<T>,
                  index,
                  totalItems: items.length,
                  getItemKey,
                  getItemLabel,
                  renderItem,
                })
              : getItems({
                  grouped: false,
                  item: item as T,
                  index,
                  totalItems: items.length,
                  getItemKey,
                  getItemLabel,
                  renderItem,
                })
          }
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

type GetItemsProps<T> = (
  | {
      grouped: true;
      group: GroupedItem<T>;
      item?: undefined;
    }
  | {
      grouped?: false;
      item: T;
      group?: undefined;
    }
) & {
  index: number;
  totalItems: number;
  getItemKey: (item: T) => string | number;
  getItemLabel: (item: T) => string;
  renderItem?: (item: T) => ReactNode;
};

function getItems<T>({
  grouped,
  group,
  item,
  index,
  totalItems,
  getItemKey,
  getItemLabel,
  renderItem,
}: GetItemsProps<T>) {
  if (grouped) {
    return (
      <ComboboxGroup key={`group-${group.value}`} items={group.items}>
        <ComboboxLabel>{group.value}</ComboboxLabel>
        <ComboboxCollection>
          {(item) => (
            <ComboboxItem key={getItemKey(item)} value={item}>
              {renderItem ? renderItem(item) : getItemLabel(item)}
            </ComboboxItem>
          )}
        </ComboboxCollection>
        {index < totalItems - 1 && <ComboboxSeparator />}
      </ComboboxGroup>
    );
  } else {
    return (
      <ComboboxItem key={getItemKey(item)} value={item}>
        {renderItem ? renderItem(item) : getItemLabel(item)}
      </ComboboxItem>
    );
  }
}
